import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlinePlus, HiOutlineVariable } from 'react-icons/hi';
import TextEdit from '../../../../molecules/TextEdit';
import { TemplateVariable, VARIABLE_FORMAT_LABELS } from '../../../../../types/communication';
import { isValidVariableName } from '../../../../../utils/templateVariables';

interface VariablePickerProps {
    variables: TemplateVariable[];
    onPick: (name: string) => void;
    disabled?: boolean;
    compact?: boolean;
    align?: 'left' | 'right';
}

const VariablePicker: React.FC<VariablePickerProps> = ({ variables, onPick, disabled, compact, align = 'right' }) => {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const filtered = useMemo(() => {
        const t = term.trim().toLocaleLowerCase('pt-BR');
        if (!t) return variables;
        return variables.filter((v) => `${v.name} ${v.label}`.toLocaleLowerCase('pt-BR').includes(t));
    }, [term, variables]);

    const candidate = term.trim();
    const canCreate = isValidVariableName(candidate) && !variables.some((v) => v.name === candidate);

    const pick = (name: string) => {
        onPick(name);
        setTerm('');
        setOpen(false);
    };

    return (
        <div ref={rootRef} className="relative inline-block">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                className={`inline-flex items-center gap-1 rounded-md border border-dashed border-teal-400 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 disabled:opacity-40 disabled:cursor-not-allowed ${
                    compact ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-xs'
                }`}
                title="Inserir variável"
            >
                <HiOutlineVariable size={compact ? 12 : 14} />
                {!compact && 'Variável'}
            </button>
            {open && (
                <div
                    className={`absolute z-30 mt-1 w-72 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl ${
                        align === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    <div className="px-3 pt-3 pb-1 border-b border-gray-100 dark:border-slate-700">
                        <TextEdit
                            id="variable-picker-search"
                            label="Buscar ou criar variável"
                            autoFocus
                            value={term}
                            onChange={(e) => setTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && filtered[0]) pick(filtered[0].name);
                                else if (e.key === 'Enter' && canCreate) pick(candidate);
                                else if (e.key === 'Escape') setOpen(false);
                            }}
                        />
                    </div>
                    <ul className="max-h-60 overflow-y-auto py-1">
                        {filtered.map((v) => (
                            <li key={v.name}>
                                <button
                                    type="button"
                                    onClick={() => pick(v.name)}
                                    className="w-full text-left px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-between gap-2"
                                >
                                    <span className="min-w-0">
                                        <span className="block text-sm text-gray-800 dark:text-gray-100 truncate">{v.label}</span>
                                        <span className="block text-[11px] font-mono text-gray-400 truncate">{`{{${v.name}}}`}</span>
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
                                        {VARIABLE_FORMAT_LABELS[v.format]}
                                    </span>
                                </button>
                            </li>
                        ))}
                        {!filtered.length && !canCreate && (
                            <li className="px-3 py-3 text-xs text-gray-400">
                                Nenhuma variável. Digite um nome (ex.: NOME_CLIENTE) para criar.
                            </li>
                        )}
                    </ul>
                    {canCreate && (
                        <button
                            type="button"
                            onClick={() => pick(candidate)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-teal-700 dark:text-teal-300 border-t border-gray-100 dark:border-slate-700 hover:bg-teal-50 dark:hover:bg-teal-900/30"
                        >
                            <HiOutlinePlus size={14} />
                            Criar variável <span className="font-mono">{candidate}</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default VariablePicker;
